/**
 * Rank overrides — the canon 01 Override stage (owner ruling 2026-09-22 #3).
 *
 * Two pieces, one module:
 *   - RankOverrideList: every override on a run session, with who, when, the
 *     move and the reason. Shown to everyone who can read the results — the
 *     ruling's "visible to the group" guardrail.
 *   - TiePanel: the result's ties, with the rule, and the facilitator's
 *     tie-break (S-5, ruling R13) — the same governed override, kept inside
 *     the tie, which the server lets the session's facilitator make.
 *   - RankOverrideForm: the manager's control. Calls the governed
 *     override_session_rank RPC (migration 768), which requires a written
 *     reason, records a Decision and notifies the session's people. The server
 *     decides authority; the caller only decides whether to show the form.
 *     The rest of the four-part rationale (context, alternatives, revisit
 *     when, review by) is optional and lands on the same Decision (985).
 *
 * Replaces sessionStore.addRankOverride, a client-only append nothing called.
 */
import { useMemo, useState } from 'react';
import { Field, Panel, Select, TextArea, TextInput } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { overrideSessionRank } from '@/lib/governedTransactions';
import { EMPTY_RATIONALE, toRationaleParts, type RationaleValue } from '@/lib/rationaleParts';
import { RationaleFields } from '@/components/prioritization/decisions/RationaleFields';
import { OVERRIDE_RANK_LABEL } from '@/lib/sessions/vocabulary';
import { TIE_RULE, sharedRanks, type TieGroups } from '@/lib/sessions/rankTies';
import type { RankOverride } from '@/types/prioritization/session';

/** An override's timestamp arrives as an ISO string from JSON; tolerate both. */
function formatOverrideDate(value: RankOverride['overriddenAt'] | null | undefined): string {
  if (!value) return 'Date unknown';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unknown' : date.toLocaleDateString();
}

export interface RankOverrideListProps {
  overrides: RankOverride[];
  getItemTitle: (itemId: string) => string;
  /** Resolves a user id to a name when the override carries no snapshot. */
  getUserName?: (userId: string) => string | undefined;
}

export function RankOverrideList({ overrides, getItemTitle, getUserName }: RankOverrideListProps) {
  if (overrides.length === 0) {
    return <p className="text-sm text-[var(--text-ter)]">No manual overrides were applied.</p>;
  }
  return (
    <ul className="space-y-3">
      {overrides.map((o, index) => {
        const who = o.overriddenByName || getUserName?.(o.overriddenBy) || 'A manager';
        return (
          <li key={o.id ?? index} className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-[var(--mustard)]">{getItemTitle(o.itemId)}</p>
                <p className="text-sm text-[var(--mustard)] mt-1">
                  {o.kind === 'tie_break' ? 'Tie broken: ' : ''}Rank {o.originalRank} → {o.newRank} · by {who}
                </p>
              </div>
              <span className="text-xs text-[var(--mustard)]">{formatOverrideDate(o.overriddenAt)}</span>
            </div>
            {o.rationale && (
              <p className="text-sm text-[var(--mustard)] mt-2 italic">Reason: {o.rationale}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface RankOverrideFormProps {
  sessionId: string;
  rankedItemIds: string[];
  getItemTitle: (itemId: string) => string;
  /** Called after the server accepted the override — typically a refetch. */
  onDone?: () => void | Promise<void>;
}

export function RankOverrideForm({ sessionId, rankedItemIds, getItemTitle, onDone }: RankOverrideFormProps) {
  const { showToast } = useToast();
  const [itemId, setItemId] = useState('');
  const [newRank, setNewRank] = useState('');
  const [reason, setReason] = useState('');
  const [rationale, setRationale] = useState<RationaleValue>(EMPTY_RATIONALE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () => rankedItemIds.map((id, i) => ({ value: id, label: `#${i + 1} ${getItemTitle(id)}` })),
    [rankedItemIds, getItemTitle],
  );
  const currentRank = itemId ? rankedItemIds.indexOf(itemId) + 1 : 0;
  const rank = Number.parseInt(newRank, 10);
  const rankValid = Number.isInteger(rank) && rank >= 1 && rank <= rankedItemIds.length && rank !== currentRank;
  const canSubmit = Boolean(itemId) && rankValid && reason.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    // The reason is the reasoning; the form's other parts ride with it.
    const result = await overrideSessionRank(
      sessionId, itemId, rank, reason,
      toRationaleParts({ ...rationale, rationaleReasoning: '' }),
    );
    setBusy(false);
    if (!result.ok) {
      setError(`${result.error ?? 'The override was refused.'}${result.remedy ? ` — ${result.remedy}` : ''}`);
      return;
    }
    showToast('success', 'Rank overridden — the group can see it with your reason.');
    setItemId('');
    setNewRank('');
    setReason('');
    setRationale(EMPTY_RATIONALE);
    await onDone?.();
  };

  return (
    <Panel label="Manager" title={OVERRIDE_RANK_LABEL}>
      <p className="text-sm text-[var(--text-ter)] mb-4">
        Move one item to a different rank. Your reason is required, is recorded as a decision,
        and is shown to everyone in the session results.
      </p>
      <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
        <Field label="Item" required>
          <Select
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            placeholder="Choose an item"
            options={options}
          />
        </Field>
        <Field label="New rank" required hint={currentRank ? `Now #${currentRank}` : undefined}>
          <TextInput
            type="number"
            min={1}
            max={rankedItemIds.length}
            value={newRank}
            onChange={(e) => setNewRank(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Reason" required className="mt-4">
        <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <RationaleFields
        className="mt-4"
        value={rationale}
        onChange={setRationale}
        omitReasoning
        title="More on why"
      />
      {error && (
        <p role="alert" className="mt-3 text-sm text-[var(--coral)]">
          {error}
        </p>
      )}
      <div className="mt-4 flex justify-end">
        <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit}>
          {busy ? 'Saving…' : OVERRIDE_RANK_LABEL}
        </Button>
      </div>
    </Panel>
  );
}

export interface TiePanelProps {
  sessionId: string;
  rankedItemIds: string[];
  ties: TieGroups;
  getItemTitle: (itemId: string) => string;
  /** The server would accept a tie-break from this person (get_session_rank_change_rule). */
  canBreak: boolean;
  onDone?: () => void | Promise<void>;
}

/**
 * The ties in a result, shown as ties with the rule. Breaking one puts the
 * chosen item first within its tie, with a reason; what is left of a tie of
 * three or more stays tied and can be broken the same way.
 */
export function TiePanel({ sessionId, rankedItemIds, ties, getItemTitle, canBreak, onDone }: TiePanelProps) {
  const { showToast } = useToast();
  const ranks = useMemo(() => sharedRanks(rankedItemIds, ties), [rankedItemIds, ties]);
  const [open, setOpen] = useState<number | null>(null);
  const [first, setFirst] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (ties.length === 0) return null;

  const submit = async (group: string[]) => {
    const rank = ranks[group[0]]?.rank;
    if (!first || !reason.trim() || !rank || busy) return;
    setBusy(true);
    setError(null);
    const result = await overrideSessionRank(sessionId, first, rank, reason);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'The tie could not be broken.');
      return;
    }
    showToast('success', 'Tie broken. Everyone reading the result sees it with your reason.');
    setOpen(null);
    setFirst('');
    setReason('');
    await onDone?.();
  };

  return (
    <Panel label="Ties" title={ties.length === 1 ? 'One tie' : `${ties.length} ties`}>
      <p className="text-sm text-[var(--text-ter)] mb-3" data-testid="tie-rule">{TIE_RULE}</p>
      <ul className="space-y-3">
        {ties.map((group, index) => {
          const rank = ranks[group[0]]?.rank;
          return (
            <li key={group.join('|')} className="border border-[var(--rule)] p-3" data-testid="tie-group">
              <p className="text-sm text-[var(--text-pri)]">
                <span className="font-semibold">Tied at #{rank}:</span> {group.map(getItemTitle).join(', ')}
              </p>
              {canBreak && open !== index && (
                <Button variant="secondary" size="sm" className="mt-2" onClick={() => { setOpen(index); setFirst(''); setReason(''); setError(null); }}>
                  Break this tie
                </Button>
              )}
              {canBreak && open === index && (
                <div className="mt-3 grid gap-3">
                  <Field label={`Which one ranks #${rank}?`} required>
                    <Select
                      value={first}
                      onChange={(e) => setFirst(e.target.value)}
                      placeholder="Choose an item"
                      options={group.map((id) => ({ value: id, label: getItemTitle(id) }))}
                    />
                  </Field>
                  <Field label="Reason" required hint="Recorded as an override; everyone reading the result sees it.">
                    <TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  {error && <p role="alert" className="text-sm text-[var(--coral)]">{error}</p>}
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setOpen(null)}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={() => void submit(group)} disabled={!first || !reason.trim() || busy}>
                      {busy ? 'Saving…' : 'Break the tie'}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {!canBreak && (
        <p className="mt-3 text-sm text-[var(--text-ter)]">Unless the facilitator breaks it, a tie is published as a tie.</p>
      )}
    </Panel>
  );
}
